/**
 * AI usage — admins only. One month or one whole year at a time, picked in
 * the page head (a Month / Year switch and Previous / Next). A year sums its
 * months' caps so far into one budget and charts spend per month.
 *
 * - Two summary cards split by a rule (UsageSummary): the budget as one
 *   segmented bar (spent, in flight, remaining, with alert and pace ticks)
 *   and the activity (run outcomes as one bar, tokens as a sparkline). The
 *   cap itself is set under Configuration → AI budget.
 * - One white card with three tabs (`?tab=`, so it links):
 *   - Overview (OverviewTab): the month at a glance, top ten per dimension
 *     and the daily spend;
 *   - Breakdown (BreakdownTab): the totals per process, model, person or day;
 *   - Runs (RunsTab): the history, one row per call.
 *   Breakdown and Runs share one set of filters (RunFilterBar), so moving
 *   between them keeps describing the same runs. An Overview name or day
 *   opens its runs from a clean filter set (so the numbers match the panel);
 *   "See all" opens the Breakdown; a Breakdown row opens its runs with the
 *   current filters kept.
 *
 * Money is always US dollars, written in the browser's number format.
 * Every figure comes from the ai_run ledger the shared AI service writes.
 */

import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Layers, LayoutDashboard, ListOrdered, Settings } from 'lucide-react';

import { useUsage } from '@/api/queries';
import { useSession } from '@/session/SessionContext';
import type { Period, Usage } from '@/api/types';
import { formatInt } from '@/ui/format';
import { RefreshButton } from '@/ui/RefreshButton';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { BreakdownTab } from './BreakdownTab';
import { OverviewTab } from './OverviewTab';
import { RunsTab } from './RunsTab';
import { UsageSummary } from './UsageSummary';
import { useUsagePage, type TabKey, type UsagePageState } from './useUsagePage';
import { drillPatch, isCurrentPeriod, isYear, periodLabel, shiftPeriod } from './usageUi';

const TABS: readonly { key: TabKey; name: string; icon: typeof Layers }[] = [
  { key: 'overview', name: 'Overview', icon: LayoutDashboard },
  { key: 'breakdown', name: 'Breakdown', icon: Layers },
  { key: 'runs', name: 'Runs', icon: ListOrdered },
];

export function AiUsagePage() {
  // Budget settings live on Configuration: offered only to whoever the server lets see it (admins).
  const { can } = useSession();
  const page = useUsagePage();
  const usage = useUsage(page.period);

  const head = (
    <PageHead title="AI Usage" subtitle="Spend, tokens and budget across every AI process">
      <PeriodPicker period={page.period} onKind={page.switchKind} onPeriod={page.changePeriod} />
      <RefreshButton queryKeys={[['ai']]} />
      {can('ai.budget.view') && (
        <Link className="btn btn-secondary" to="/configuration?tab=ai-budget">
          <Settings aria-hidden="true" /> Budget settings
        </Link>
      )}
    </PageHead>
  );

  if (usage.isPending) return <>{head}<LoadingCard label="Loading AI usage" /></>;
  if (usage.error) return <>{head}<LoadFailure error={usage.error} onRetry={() => void usage.refetch()} /></>;

  return (
    <>
      {head}
      <UsageSummary data={usage.data} />
      <UsageCard page={page} data={usage.data} />
    </>
  );
}

function PeriodPicker({ period, onKind, onPeriod }: { period: Period; onKind: (toYear: boolean) => void; onPeriod: (next: Period) => void }) {
  const year = isYear(period);
  return (
    <div className="period-picker">
      <div className="segmented" role="radiogroup" aria-label="Show">
        <button type="button" role="radio" aria-checked={!year} onClick={() => onKind(false)}>Month</button>
        <button type="button" role="radio" aria-checked={year} onClick={() => onKind(true)}>Year</button>
      </div>
      <div className="month-picker" role="group" aria-label={year ? 'Year' : 'Month'}>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          aria-label={year ? 'Previous year' : 'Previous month'}
          onClick={() => onPeriod(shiftPeriod(period, -1))}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
        <span className="month-label">{periodLabel(period)}</span>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          aria-label={year ? 'Next year' : 'Next month'}
          disabled={isCurrentPeriod(period)}
          onClick={() => onPeriod(shiftPeriod(period, 1))}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function UsageCard({ page, data }: { page: UsagePageState; data: Usage }) {
  const { tab, period, filters, changeFilters, openTab, fromOverview } = page;
  return (
    <div className="card list-card">
      <div className="tabs card-tabs" role="tablist" aria-label="AI Usage">
        {TABS.map(({ key, name, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`tab${tab === key ? ' is-active' : ''}`}
            onClick={() => openTab(key)}
          >
            <Icon aria-hidden="true" />
            {name}
            {key === 'runs' && <span className="count">{formatInt(data.totals.runs)}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          data={data}
          onDrill={(by, key) => fromOverview(drillPatch(by, key), 'runs')}
          onSeeAll={(by) => fromOverview({}, 'breakdown', by)}
        />
      )}
      {tab === 'breakdown' && (
        <BreakdownTab
          period={period}
          data={data}
          filters={filters}
          onFilters={changeFilters}
          view={page.breakdown}
          onView={page.changeBreakdown}
          onDrill={(by, key) => {
            // The other filters stay: the runs behind the row as it was counted.
            changeFilters(drillPatch(by, key));
            openTab('runs');
          }}
        />
      )}
      {tab === 'runs' && (
        <RunsTab period={period} data={data} filters={filters} onFilters={changeFilters} view={page.runsView} onView={page.changeRunsView} />
      )}
    </div>
  );
}
